import { useQuery } from '@tanstack/react-query';
import type { InvoiceResponse, InvoiceXmlResponse } from '@k-management/shared';
import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { apiRequest } from '../../lib/api.js';
import { treasuryKeys } from './treasury-ui.js';
import '../quotes/documents/documents.css';

/**
 * The stylesheet of AssoSoftware for the electronic invoice (07/10/2026): the
 * standard layout the Italian accounting software use, distributed free by
 * AssoSoftware to be built into them. Kept as published in
 * apps/web/public/fatturapa (update by replacing the file).
 */
const STYLESHEET = '/fatturapa/FoglioStileAssoSoftware.xsl';

/** The XML of the invoice through the stylesheet, as a whole HTML page with its title. */
async function render(xml: string, title: string): Promise<string> {
  const parser = new DOMParser();
  const invoice = parser.parseFromString(xml, 'application/xml');
  if (invoice.getElementsByTagName('parsererror').length) {
    throw new Error('Il file salvato non è un XML leggibile.');
  }
  const response = await fetch(STYLESHEET);
  if (!response.ok) throw new Error('Foglio di stile non disponibile.');
  const stylesheet = parser.parseFromString(await response.text(), 'application/xml');
  const processor = new XSLTProcessor();
  processor.importStylesheet(stylesheet);
  const page = processor.transformToDocument(invoice);
  if (!page?.documentElement) throw new Error('Il foglio di stile non ha prodotto la fattura.');
  const html = new XMLSerializer().serializeToString(page);
  const head = `<meta charset="utf-8"><title>${title.replace(/[<>&"]/g, '')}</title>`;
  return /<head[^>]*>/i.test(html)
    ? html.replace(/<head([^>]*)>/i, `<head$1>${head}`)
    : `<!doctype html><html><head>${head}</head><body>${html}</body></html>`;
}

/**
 * The electronic invoice as a page to print or save as PDF (07/10/2026),
 * laid out by the AssoSoftware stylesheet like the copy the user knows from
 * Gestify. The page is drawn in a sandboxed frame without scripts: the XML
 * comes from a supplier.
 */
export function EInvoicePage() {
  const { id = '' } = useParams();
  const frame = useRef<HTMLIFrameElement>(null);
  const invoice = useQuery({
    queryKey: treasuryKeys.invoice(id),
    queryFn: () => apiRequest<InvoiceResponse>(`/treasury/invoices/${id}`),
  });
  const stored = useQuery({
    queryKey: [...treasuryKeys.invoice(id), 'xml'],
    queryFn: () => apiRequest<InvoiceXmlResponse>(`/treasury/invoices/${id}/xml`),
    retry: false,
  });
  // The name the PDF and the XML are saved with.
  const title = invoice.data
    ? `${invoice.data.direction === 'emessa' ? 'Fattura' : 'Fattura passiva'} ${invoice.data.number} - ${invoice.data.counterpartyName}`
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : 'Fattura elettronica';
  const page = useQuery({
    queryKey: [...treasuryKeys.invoice(id), 'xml', 'page', title],
    queryFn: () => render(stored.data!.xml, title),
    enabled: Boolean(stored.data && invoice.data),
    staleTime: Infinity,
  });

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = 'K-Management';
    };
  }, [title]);

  const download = () => {
    if (!stored.data) return;
    const blob = new Blob([stored.data.xml], { type: 'application/xml' });
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${title}.xml`,
    });
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="doc-screen">
      <div className="doc-toolbar">
        <Link to={`/tesoreria/fatture/${id}`}>← Torna alla fattura</Link>
        <span className="spacer" />
        <span className="muted small">Nella stampa scegli «Salva come PDF».</span>
        <button type="button" onClick={download} disabled={!stored.data}>
          Scarica XML
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => frame.current?.contentWindow?.print()}
          disabled={!page.data}
        >
          Stampa o salva PDF
        </button>
      </div>
      {(stored.isPending || page.isPending) && !stored.isError && (
        <p className="doc-page">Caricamento…</p>
      )}
      {stored.isError && (
        <p className="doc-page">
          La fattura elettronica non è ancora stata letta da Gestify: aprila dalla scheda della
          fattura con «Apri, stampa o salva PDF».
        </p>
      )}
      {page.isError && <p className="doc-page error">{page.error.message}</p>}
      {page.data && (
        <iframe
          ref={frame}
          className="einvoice-frame"
          title={title}
          srcDoc={page.data}
          // No scripts; same origin only to print the frame from the toolbar.
          sandbox="allow-same-origin allow-modals"
        />
      )}
    </div>
  );
}
